import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Facebook, 
  Check, 
  RefreshCw, 
  AlertCircle, 
  ExternalLink,
  FileText,
  Clock,
  User,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface FacebookPage {
  id: string;
  page_id: string;
  page_name: string;
  page_category: string | null;
  is_active: boolean;
  lead_form_ids: string[] | null;
  webhook_subscribed: boolean;
  created_at: string;
  updated_at: string;
  facebook_oauth_tokens: {
    fb_user_name: string;
    token_expiry: string;
  } | null;
}

export default function FacebookSettings() {
  const [isConnecting, setIsConnecting] = useState(false);
  const queryClient = useQueryClient();

  // Fetch current user's app_user_id
  const { data: currentUser } = useQuery({
    queryKey: ['current-app-user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();
      
      return data;
    }
  });

  // Fetch connected pages
  const { data: connectedPages, isLoading, refetch } = useQuery({
    queryKey: ['facebook-connected-pages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('facebook_connected_pages')
        .select(`
          *,
          facebook_oauth_tokens(fb_user_name, token_expiry)
        `)
        .eq('is_active', true)
        .order('page_name');

      if (error) throw error;
      return data as FacebookPage[];
    }
  });

  // Listen for OAuth popup messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'facebook_connected') {
        toast.success('Facebook conectado com sucesso!', {
          description: `${event.data.pages} página(s) conectada(s)`
        });
        refetch();
        setIsConnecting(false);
      } else if (event.data.type === 'facebook_error') {
        toast.error('Erro ao conectar Facebook', {
          description: event.data.message
        });
        setIsConnecting(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [refetch]);

  // Start OAuth flow
  const handleConnect = useCallback(async () => {
    if (!currentUser?.id) {
      toast.error('Usuário não encontrado');
      return;
    }

    setIsConnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke('facebook-oauth', {
        body: { app_user_id: currentUser.id }
      });

      if (error) throw error;

      if (data?.authUrl) {
        // Open OAuth popup
        const popup = window.open(
          data.authUrl,
          'facebook_oauth',
          'width=600,height=700,scrollbars=yes'
        );

        // Check if popup was blocked
        if (!popup) {
          toast.error('Popup bloqueado', {
            description: 'Por favor, permita popups para este site'
          });
          setIsConnecting(false);
        }
      }
    } catch (err: any) {
      console.error('Error starting OAuth:', err);
      toast.error('Erro ao iniciar conexão', {
        description: err.message
      });
      setIsConnecting(false);
    }
  }, [currentUser?.id]);

  // Deactivate a page
  const deactivatePage = useMutation({
    mutationFn: async (pageId: string) => {
      const { error } = await supabase
        .from('facebook_connected_pages')
        .update({ is_active: false })
        .eq('id', pageId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facebook-connected-pages'] });
      toast.success('Página desconectada');
    },
    onError: (error: any) => {
      toast.error('Erro ao desconectar página', { description: error.message });
    }
  });

  const isConnected = (connectedPages?.length || 0) > 0;
  const firstPage = connectedPages?.[0];
  const tokenExpiry = firstPage?.facebook_oauth_tokens?.token_expiry 
    ? new Date(firstPage.facebook_oauth_tokens.token_expiry)
    : null;
  const daysUntilExpiry = tokenExpiry ? differenceInDays(tokenExpiry, new Date()) : null;

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Configurações do Facebook</h1>
        <p className="text-muted-foreground mt-1">
          Conecte suas páginas do Facebook para importar leads automaticamente
        </p>
      </div>

      {/* Connection Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Facebook className="h-6 w-6 text-blue-500" />
              </div>
              <div>
                <CardTitle>Facebook Business</CardTitle>
                <CardDescription>
                  Lead Ads, Páginas e Métricas de Anúncios
                </CardDescription>
              </div>
            </div>
            {isConnected && (
              <Badge variant="outline" className="text-green-600 border-green-600">
                <Check className="h-3 w-3 mr-1" />
                Conectado
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : isConnected ? (
            <>
              {/* Connected User Info */}
              <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                <User className="h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="font-medium">
                    {firstPage?.facebook_oauth_tokens?.fb_user_name || 'Usuário Facebook'}
                  </p>
                  {tokenExpiry && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Token expira em {daysUntilExpiry} dias
                      {daysUntilExpiry !== null && daysUntilExpiry < 14 && (
                        <Badge variant="destructive" className="ml-2 text-xs">
                          Reconectar em breve
                        </Badge>
                      )}
                    </p>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={handleConnect} disabled={isConnecting}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${isConnecting ? 'animate-spin' : ''}`} />
                  Reconectar
                </Button>
              </div>

              <Separator />

              {/* Connected Pages List */}
              <div>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  Páginas Conectadas
                  <Badge variant="secondary">{connectedPages.length}</Badge>
                </h3>
                <div className="space-y-2">
                  {connectedPages.map((page) => (
                    <div
                      key={page.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Facebook className="h-5 w-5 text-blue-500" />
                        </div>
                        <div>
                          <p className="font-medium">{page.page_name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {page.page_category && (
                              <span>{page.page_category}</span>
                            )}
                            {page.lead_form_ids && page.lead_form_ids.length > 0 && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <FileText className="h-3 w-3" />
                                  {page.lead_form_ids.length} formulário(s)
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={`https://facebook.com/${page.page_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-muted rounded-md transition-colors"
                        >
                          <ExternalLink className="h-4 w-4 text-muted-foreground" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Info about permanent tokens */}
              <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-blue-700 dark:text-blue-300">
                    Tokens de Página Permanentes
                  </p>
                  <p className="text-blue-600 dark:text-blue-400 mt-1">
                    Os tokens das páginas acima são permanentes e não expiram. 
                    Você só precisa reconectar se quiser adicionar novas páginas ou 
                    se revogar o acesso manualmente no Facebook.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <p className="text-muted-foreground">
                Conecte sua conta do Facebook Business para:
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Importar leads automaticamente de todas as suas páginas
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Receber leads em tempo real via webhook
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Sincronizar métricas de campanhas de anúncios
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  Tokens permanentes - conecte uma vez, use para sempre
                </li>
              </ul>
              <Button 
                onClick={handleConnect} 
                disabled={isConnecting || !currentUser}
                className="w-full sm:w-auto"
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <Facebook className="mr-2 h-4 w-4" />
                    Conectar Facebook Business
                  </>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Prerequisites Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Pré-requisitos</CardTitle>
          <CardDescription>
            Configurações necessárias no Meta Business antes de conectar
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ol className="list-decimal list-inside space-y-3 text-sm">
            <li>
              <span className="font-medium">Ter um Facebook App criado</span>
              <p className="text-muted-foreground ml-5 mt-1">
                Acesse{' '}
                <a 
                  href="https://developers.facebook.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  developers.facebook.com
                </a>{' '}
                e crie um app do tipo "Business"
              </p>
            </li>
            <li>
              <span className="font-medium">Configurar a URL de redirecionamento OAuth</span>
              <p className="text-muted-foreground ml-5 mt-1">
                Em Facebook Login {'>'} Settings, adicione:
              </p>
              <code className="block ml-5 mt-2 p-2 bg-muted rounded text-xs break-all">
                https://irtygpadfbavrqhldhzm.supabase.co/functions/v1/facebook-oauth/callback
              </code>
            </li>
            <li>
              <span className="font-medium">Adicionar FB_APP_ID e FB_APP_SECRET nos Secrets</span>
              <p className="text-muted-foreground ml-5 mt-1">
                Configure as credenciais do app no painel de Secrets do Supabase
              </p>
            </li>
            <li>
              <span className="font-medium">Solicitar permissões necessárias</span>
              <p className="text-muted-foreground ml-5 mt-1">
                pages_show_list, pages_read_engagement, leads_retrieval, ads_read, business_management
              </p>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
